import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { getAllPlans, createStripeCheckout } from '../contexts/db.Plans';
import { detectCurrency, getCurrencySymbol, getPlanPrice } from '../contexts/geolocation';
import '../styles/pages/Plans.css';

function Plans({ language }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currency, setCurrency] = useState('EUR');

  const location = useLocation();
  const navigate = useNavigate();
  const t = translations[language];

  // Detect currency based on geolocation
  useEffect(() => {
    detectCurrency().then(setCurrency);
  }, []);
  
  // Get userId from location state or localStorage
  const fromLogin = location.state?.fromLogin;
  const subscriptionStatus = location.state?.subscriptionStatus;
  const userId = location.state?.userId || (() => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      return userData?.id;
    } catch { return null; }
  })();
  
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);

        const plansResponse = await getAllPlans();
        // API shape can be either an array OR { status: true, plans: [...] }
        const normalizedPlans = Array.isArray(plansResponse)
          ? plansResponse
          : Array.isArray(plansResponse?.plans)
            ? plansResponse.plans
            : [];

        setPlans(normalizedPlans);
      } catch (fetchError) {
        console.error('Error loading plans:', fetchError);
        setError(t.subscriptionFailed || 'Failed to load subscription plans. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [language]);

  // Handle plan selection
  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  // Handle subscription submission - redirect to Stripe checkout
  const handleSubscribe = async () => {
    if (!selectedPlan) {
      setError(t.selectPlanFirst || 'Please select a plan first.');
      return;
    }
    
    if (!userId) {
      setError(t.missingUserInfo || 'User information is missing. Please register again.');
      setTimeout(() => navigate('/register'), 2000);
      return;
    }
    
    try {
      setSubscribing(true);
      setError(null);

      // Call Stripe checkout endpoint
      const response = await createStripeCheckout(userId, selectedPlan, currency);

      console.log("Stripe Response:", response);
      
      // Redirect to Stripe checkout page
      if (response && response.stripe_url) {
        console.log("Redirecting to:", response.stripe_url);
        window.location.href = response.stripe_url;
      } else {
        if (response.data && response.data.stripe_url) {
          console.log("Redirecting to:", response.data.stripe_url);
          window.location.href = response.data.stripe_url;
        }
        else {
          throw new Error('No checkout URL received from server');
        }
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(t.checkoutFailed || 'Failed to start checkout. Please try again.');
      setSubscribing(false);
    }
  };


  if (loading) {
    return (
      <div className="plans-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>{t.loadingPlans || 'Loading subscription plans...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="plans-container">
      {fromLogin && (
        <div className="subscription-required-banner">
          {t.subscriptionRequired || 'A subscription is required to access the application. Please choose a plan below.'}
        </div>
      )}
      
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      
      <div className="plans-header">
        <h1>{t.choosePlan || 'Choose Your Subscription Plan'}</h1>
        <p>{t.selectPlanDescription || 'Select the plan that best fits your needs'}</p>
        
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="plans-grid">
        {(Array.isArray(plans) ? plans : []).map((plan) => (
          <div 
            key={plan.id} 
            className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''}`}
            onClick={() => {
              handlePlanSelect(plan.id);
            }}
          >
            <div className="free-trial-badge">
              {t.freeTrial || 'Free trial 3 days'}
            </div>
            <div className="plan-header">
              <h3>{plan.name}</h3>
              <div className="plan-price">
                <span className="currency">{getCurrencySymbol(getPlanPrice(plan, currency).currency)}</span>
                <span className="amount">{getPlanPrice(plan, currency).price}</span>
                <span className="period">
                  /{plan.billing_cycle === '12_months'
                    ? (t.twelveMonths || '12 months')
                    : plan.billing_cycle === '3_months'
                      ? (t.threeMonths || '3 months')
                      : (t.monthly || 'month')}
                </span>
              </div>
            </div>
            <div className="plan-description">
              {plan.description}
            </div>
            <ul className="plan-features">
              <li>{t.planFeature1 || 'Regular and personalized phone follow-up'}</li>
              <li>{t.planFeature2 || 'Access to all levels in the application'}</li>
              <li>{t.planFeature3 || 'Buy Heroes using your collected coins'}</li>
            </ul>
            <button 
              className={`select-plan-btn ${selectedPlan === plan.id ? 'selected' : ''}`}
            >
              {selectedPlan === plan.id ? (t.selected || 'Selected') : (t.selectPlan || 'Select Plan')}
            </button>
          </div>
        ))}
      </div>

      <div className="commitment-info">
        <div className="info-icon">i</div>
        <div className="info-text">
          {t.commitmentInfo || 'Commitment of 3 or 12 months, can be canceled anytime after'}
        </div>
      </div>

      <div className="action-buttons">
        {/* <button 
          className="back-button"
          onClick={() => fromLogin ? navigate('/') : navigate('/register')}
        >
          {t.back || 'Back'}
        </button> */}
        <button 
          className="subscribe-button"
          disabled={!selectedPlan || subscribing}
          onClick={handleSubscribe}
        >
          {subscribing ? (
            <>
              <span className="button-spinner"></span>
              {t.redirectingToPayment || 'Redirecting to payment...'}
            </>
          ) : (t.subscribe || 'Subscribe')}
        </button>
      </div>
    </div>
  );
}

export default Plans;
